import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupplierAuthContext = createContext(null);

export const SupplierAuthProvider = ({ children }) => {
  const [supplier, setSupplier] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('supplier_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/supplier-portal/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSupplier(response.data);
      } catch (error) {
        console.error('Token validation failed:', error);
        localStorage.removeItem('supplier_token');
        setToken(null);
        setSupplier(null);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/supplier-portal/login`, {
      email,
      password
    });
    
    const { session_token, supplier: supplierData } = response.data;
    localStorage.setItem('supplier_token', session_token);
    setToken(session_token);
    setSupplier(supplierData);
    return supplierData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/supplier-portal/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('supplier_token');
      setToken(null);
      setSupplier(null);
    }
  };

  const refreshSupplier = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/supplier-portal/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupplier(response.data);
    } catch (error) {
      console.error('Failed to refresh supplier:', error);
    }
  };

  return (
    <SupplierAuthContext.Provider value={{
      supplier,
      token,
      loading,
      isAuthenticated: !!supplier,
      login,
      logout,
      refreshSupplier
    }}>
      {children}
    </SupplierAuthContext.Provider>
  );
};

export const useSupplierAuth = () => {
  const context = useContext(SupplierAuthContext);
  if (!context) {
    throw new Error('useSupplierAuth must be used within a SupplierAuthProvider');
  }
  return context;
};
